import { useState } from 'react';
import { EstadoSinPermiso } from '../../componentes/EstadoPagina';
import { Icono } from '../../componentes/Icono';
import { Segmentos } from '../../componentes/Segmentos';
import { rolesDe } from '../../logica/acceso';
import { useCabecera } from '../../marco/cabecera';
import { usePermisos } from '../../permisos/permisos';
import { rutaConApartado, type PropsPantalla } from '../tipos';
import { Ajustes } from './Ajustes';
import { Automaticos } from './Automaticos';
import { ListaCampanas } from './ListaCampanas';
import { Plantillas } from './Plantillas';
import { APARTADOS, apartadoDe, AYUDA, SUBTITULO, vistaDe, type Apartado } from './reglas';
import { Resumen } from './Resumen';
import '../../componentes/apartado.css';
import '../avisos/avisos.css';
import '../canales/canales.css';
import './campanas.css';

/**
 * «Campañas» (entrada `campanas`; 12.1 a 12.7): la barra Campañas · Mensajes automáticos · Plantillas en `?apartado=`, con
 * una línea que dice qué es cada uno, y «Ajustes» en la línea del título (12.7 en un RV Panel; `?apartado=ajustes` lo
 * abre). `?apartado=resultados` es Campañas con el resumen abierto. Quien solo tiene `campanas.resultados.ver_resumen` (el
 * Socio, G459, G776) ve solo «Resultados de campañas», sin lista ni «Nueva campaña».
 */
export interface PropsApartado extends PropsPantalla {
    /** Abre los Ajustes de campañas (12.7) en su panel. */
    abrirAjustes: () => void;
    irA: (apartado: Apartado | 'resultados', extra?: string) => void;
}

export function Campanas(props: PropsPantalla) {
    const { busqueda, ruta, navegar, movil, membresia, nombreNegocio } = props;
    const { puede } = usePermisos();
    const vista = vistaDe(puede);
    const activo = apartadoDe(busqueda.get('apartado'), vista);
    const [ajustes, setAjustes] = useState(busqueda.get('apartado') === 'ajustes');
    const base = ruta.split('?')[0] ?? ruta;
    const irA = (apartado: Apartado | 'resultados', extra?: string) => {
        navegar(`${rutaConApartado(base, apartado)}${extra === undefined ? '' : `&${extra}`}`);
    };

    if (vista.soloResumen) {
        return <SoloResumen {...props} />;
    }
    if (activo === null) {
        return <EstadoSinPermiso seccion="las campañas" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />;
    }
    const visibles = APARTADOS.filter((a) => vista.apartados.includes(a.valor));
    const elegido = visibles.find((a) => a.valor === activo) ?? visibles[0];
    const Apartado = activo === 'campanas' ? ListaCampanas : activo === 'automaticos' ? Automaticos : Plantillas;

    return (
        <div className="rv-en-pantalla">
            <div className="rv-cp-barra">
                <div className="rv-en-barra">
                    <Segmentos
                        etiqueta="Apartados de campañas"
                        movil={movil}
                        items={visibles.map((a) => ({ etiqueta: a.etiqueta, corta: a.corta, icono: a.icono }))}
                        valor={elegido === undefined ? 0 : visibles.indexOf(elegido)}
                        onCambio={(i) => {
                            const a = visibles[i];
                            if (a !== undefined) {
                                irA(a.valor);
                            }
                        }}
                    />
                </div>
                {elegido !== undefined && (
                    <p className="rv-cp-pista">
                        <Icono nombre={elegido.icono} />
                        <span>{elegido.pista}</span>
                    </p>
                )}
            </div>
            <Apartado key={activo} {...props} abrirAjustes={() => { setAjustes(true); }} irA={irA} />
            {ajustes && <Ajustes {...props} onCerrar={() => { setAjustes(false); }} />}
        </div>
    );
}

/** El Socio (G776): «Resultados de campañas», agregado y en solo lectura. */
function SoloResumen(props: PropsPantalla) {
    useCabecera({ ayuda: AYUDA.socio, acciones: [], subtitulo: SUBTITULO.socio });
    return (
        <div className="rv-en-pantalla">
            <Resumen ops={props.ops} negocio={props.negocio} movil={props.movil} socio />
        </div>
    );
}
